import { useMemo } from 'react'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { calculateMonthlyMetrics } from '@/engine/calculateMonthlyMetrics'
import { LOCATION_DATA } from '@/data/locationData'
import { BASE_MATRIX_DATA } from '@/data/planPresets'
import { useSimulationData } from './useSimulationData'
import type { MonthlyMetrics } from '@/types'

export const useCalculation = () => {
  const config = useEnergyStore((s) => s.config)
  const plans = useEnergyStore((s) => s.plans)
  const selectedPlanName = useEnergyStore((s) => s.selectedPlan)
  const selectedConfigId = useEnergyStore((s) => s.selectedConfig)
  const { data: simulationMonthlyData } = useSimulationData()

  const selectedPlan = useMemo(() => {
    return plans.find((p) => p.plan === selectedPlanName) || plans[0] || BASE_MATRIX_DATA[0]!
  }, [plans, selectedPlanName])

  const results = useMemo(() => {
    const locData = LOCATION_DATA[config.location]
    const locMult = config.location === 'Custom' ? (config.customLocationMultiplier ?? 1.0) : (locData?.multiplier ?? 1.0)
    const locPriceMult = locData?.priceMult ?? 1.0

    const monthlyResults: MonthlyMetrics[] = []
    let totalEvPetrolSavings = 0
    
    // Simulate 16 months as per legacy dashboard
    for (let i = 0; i < 16; i++) {
      const simAdjust = simulationMonthlyData.length > 0 ? (simulationMonthlyData[i % 12] ?? 0) : 0
      const metrics = calculateMonthlyMetrics(
        i,
        config,
        selectedPlan,
        selectedConfigId,
        locMult,
        locPriceMult,
        simAdjust
      )
      monthlyResults.push(metrics)

      // Calculate EV petrol savings if included in ROI
      if (config.includeEvInRoi) {
        const distance = config.evDistance || 0
        let monthlyKm = 0
        if (config.evDistMode === 'monthly') {
          monthlyKm = distance
        } else if (config.evDistMode === 'weekly') {
          monthlyKm = distance * 4.345
        } else {
          monthlyKm = distance * 30.4 // standard days in month
        }
        const petrolEfficiency = config.petrolEfficiency ?? 8.0
        const petrolPrice = config.petrolPrice ?? 2.80
        const petrolRate = petrolEfficiency * petrolPrice // $/100km equivalent rate
        const monthlyPetrolCost = (monthlyKm / 100) * petrolRate

        // EV ongoing penalties
        const rucRate = config.evRucRate ?? 76.0
        const monthlyRuc = (monthlyKm / 1000) * rucRate

        const annualPenalties = (config.evTireWear ?? 200) + 
                                (config.evInsurancePremium ?? 300) + 
                                (config.evServicing ?? 150) + 
                                (config.evDepreciation ?? 2500)
        const monthlyPenalties = annualPenalties / 12
        const totalMonthlyEvRunningCost = metrics.monthlyEvCost + monthlyRuc + monthlyPenalties

        const monthlySavings = monthlyPetrolCost - totalMonthlyEvRunningCost
        totalEvPetrolSavings += monthlySavings
      }
    }

    // Aggregate totals
    const totalNetCost = monthlyResults.reduce((acc, m) => acc + m.netCost, 0)
    const totalCostWithoutSolar = monthlyResults.reduce((acc, m) => acc + m.costWithoutSolar, 0)
    const totalGenKwh = monthlyResults.reduce((acc, m) => acc + m.solarGenerationKwh, 0)
    const totalGridKwh = monthlyResults.reduce((acc, m) => acc + m.gridConsumptionKwh, 0)
    const totalEvCost = monthlyResults.reduce((acc, m) => acc + m.monthlyEvCost, 0)
    const totalExportCredit = monthlyResults.reduce((acc, m) => acc + m.exportCredit, 0)
    const totalSelfUseSavings = monthlyResults.reduce((acc, m) => acc + m.selfUseSavings, 0)
    const totalLoadKwh = monthlyResults.reduce((acc, m) => acc + m.totalLoadKwh, 0)
    const totalSelfConsKwh = monthlyResults.reduce((acc, m) => acc + m.selfConsKwh, 0)
    
    // Averages
    const avgMonthlyNetCost = totalNetCost / 16
    const avgEvCostPer100km = monthlyResults.reduce((acc, m) => acc + m.evCostPer100km, 0) / 16

    // Total ROI Investment (Capital)
    const activeSystemCost = config.useSolar !== false ? config.systemCost : 0
    const activeEvCost = config.includeEvInRoi ? (config.evUpfrontCost ?? 0) : 0
    const totalCapitalCost = activeSystemCost + activeEvCost

    // ROI calculation (simplified version of compounding ROI)
    let yearsToPayback = 0
    const baseSolarSavings = totalCostWithoutSolar - totalNetCost
    const totalActualSavings = baseSolarSavings + totalEvPetrolSavings

    if (totalActualSavings > 0 && totalCapitalCost > 0) {
      const annualBenefit = (totalActualSavings / 16) * 12
      let remainingCost = totalCapitalCost
      let currentAnnualBenefit = annualBenefit
      
      while (remainingCost > 0 && yearsToPayback < 25) {
        yearsToPayback++
        remainingCost -= currentAnnualBenefit
        if (config.useCompounding) {
          currentAnnualBenefit *= (1 + config.priceHike / 100)
        }
      }
    } else if (totalActualSavings <= 0 && totalCapitalCost > 0) {
      yearsToPayback = Infinity
    }

    return {
      monthlyResults,
      totals: {
        netCost: totalNetCost * (12 / 16),
        costWithoutSolar: totalCostWithoutSolar * (12 / 16),
        solarBenefit: (totalCostWithoutSolar - totalNetCost) * (12 / 16),
        genKwh: totalGenKwh * (12 / 16),
        gridKwh: totalGridKwh * (12 / 16),
        evCost: totalEvCost * (12 / 16),
        evPetrolSavings: totalEvPetrolSavings * (12 / 16),
        exportCredit: totalExportCredit * (12 / 16),
        selfUseSavings: totalSelfUseSavings * (12 / 16),
        totalLoadKwh: totalLoadKwh * (12 / 16),
        selfConsKwh: totalSelfConsKwh * (12 / 16),
      },
      averages: {
        monthlyNetCost: avgMonthlyNetCost,
        evCostPer100km: avgEvCostPer100km,
      },
      roi: {
        years: yearsToPayback,
        totalCapitalCost,
      }
    }
  }, [config, selectedPlan, selectedConfigId, simulationMonthlyData])

  return results
}
